import React, { useEffect, useState } from 'react'
import CarrierService from '../../../../../shared/services/Carrier.service'
import { MDBCol, MDBRow } from 'mdb-react-ui-kit'
import MDB4Switch from '../../../../../components/forms/MDB4Switch'
import { UIInputCheckbox } from '../../../../../components/forms/form-fields'
import UserProfileService from '../../../../../shared/services/UserProfile.service'
import LoadingSpinner from '../../../../../components/shared/LoadingSpinner.component'
import { toast } from 'react-toastify'

import './CarrierManagement.component.scss'

const CarrierManagement = () => {
  const [carriers, setCarriers] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [isSavingAll, setIsSavingAll] = useState(false)

  useEffect(() => {
    fetchActiveCarriers()
  }, [])

  const searchActiveCarriers = async () => {
    const carriers = await CarrierService.search({
      pagination: false,
      search: { c_active: 1 },
      orderBy: { c_name: 'ASC' },
    })

    return (
      carriers?.models?.map(carrier => {
        return {
          id: carrier.id,
          name: carrier.c_name,
          disabled_uplines: carrier.disabled_uplines || [],
          sibling_ids: carrier.sibling_ids || [],
          description: carrier.c_description,
        }
      }) || []
    )
  }

  const fetchActiveCarriers = async () => {
    try {
      setCarriers(await searchActiveCarriers())
      setIsLoading(false)
    } catch (error) {
      toast.error('Failed to fetch active carriers', {
        position: toast.POSITION.TOP_RIGHT,
      })
      setIsLoading(false)
    }
  }

  const toggleCarrier = async carrier => {
    let disabled = false
    const user_id = UserProfileService.getUserId()

    if (carrier.disabled_uplines.includes(user_id)) {
      carrier.disabled_uplines = carrier.disabled_uplines.filter(item => item !== user_id)
      disabled = false
    } else {
      carrier.disabled_uplines.push(user_id)
      disabled = true
    }

    try {
      await CarrierService.update(carrier.id, {
        disabled_uplines: JSON.stringify(carrier.disabled_uplines),
      })

      let updated_carriers = carriers
      updated_carriers = updated_carriers.map(item => (item.id === carrier.id ? { ...item, ...carrier } : item))
      setCarriers(updated_carriers)

      toast.success(`${carrier.name} is ${disabled ? 'disabled' : 'enabled'}.`, { position: toast.POSITION.TOP_RIGHT })
    } catch (error) {
      toast.error(`Failed to ${disabled ? 'disable' : 'enable'} ${carrier.name}`, {
        position: toast.POSITION.TOP_RIGHT,
      })
    }
  }

  const userId = UserProfileService.getUserId()
  const enabledCount = carriers.filter(carrier => !carrier.disabled_uplines?.includes(userId)).length
  const isAllEnabled = carriers.length > 0 && enabledCount === carriers.length
  const isPartlyEnabled = enabledCount > 0 && !isAllEnabled

  const toggleAllCarriers = async () => {
    const user_id = UserProfileService.getUserId()
    const enable = !isAllEnabled
    let saved = 0
    let total = 0

    setIsSavingAll(true)

    try {
      const pending = (await searchActiveCarriers()).filter(
        carrier => carrier.disabled_uplines.includes(user_id) === enable
      )
      total = pending.length

      for (const carrier of pending) {
        const disabled_uplines = enable
          ? carrier.disabled_uplines.filter(item => item !== user_id)
          : [...carrier.disabled_uplines, user_id]

        try {
          await CarrierService.update(carrier.id, {
            disabled_uplines: JSON.stringify(disabled_uplines),
          })
          saved++
        } catch (error) {}
      }

      if (saved === total) {
        toast.success(`All carriers ${enable ? 'enabled' : 'disabled'}`, { position: toast.POSITION.TOP_RIGHT })
      } else {
        toast.error(`${saved} of ${total} carriers updated`, { position: toast.POSITION.TOP_RIGHT })
      }
    } catch (error) {
      toast.error(`Failed to ${enable ? 'enable' : 'disable'} all carriers`, {
        position: toast.POSITION.TOP_RIGHT,
      })
    }

    await fetchActiveCarriers()
    setIsSavingAll(false)
  }

  return (
    <div id='CarrierManagementComponent'>
      {isLoading ? (
        <div className='loading-spinner'>
          <LoadingSpinner size='md' isActive={true} />
        </div>
      ) : (
        <>
          {carriers.length > 0 && (
            <div className='carrier-select-all'>
              <div>
                <UIInputCheckbox
                  id='carrier_select_all'
                  name='carrier_select_all'
                  label='Select All'
                  checked={isAllEnabled}
                  indeterminate={isPartlyEnabled}
                  disabled={isSavingAll}
                  onChange={toggleAllCarriers}
                />
                <div className='carrier-select-all-hint'>
                  {isAllEnabled
                    ? 'Uncheck to disable every carrier for your direct downline.'
                    : 'Check to enable every carrier for your direct downline.'}
                </div>
              </div>
              <span className='carrier-select-all-count'>
                {isSavingAll ? 'Saving…' : `${enabledCount} of ${carriers.length} enabled`}
              </span>
            </div>
          )}
          <MDBRow>
            {carriers.map(carrier => {
              return (
                <MDBCol key={`carrier-${carrier.id}`} size='12' sm='6' className='carrier-toggle'>
                  <MDB4Switch
                    labelLeft={
                      <div className='carrier-toggle-label'>
                        <div>{carrier.name}:</div>
                        {carrier.sibling_ids.length > 0 && (
                          <div className='carrier-toggle-description'>{carrier.description}</div>
                        )}
                      </div>
                    }
                    labelRight={''}
                    name={carrier.name}
                    checked={!carrier.disabled_uplines?.includes(UserProfileService.getUserId())}
                    onChange={event => toggleCarrier(carrier)}
                  />
                </MDBCol>
              )
            })}
          </MDBRow>
        </>
      )}
    </div>
  )
}

export default CarrierManagement
